import { View } from "@/components/ui";
import { AccountInfoCard } from "@/components/shared/account-info-card";
import { fullName } from "@/lib/helpers/string";
import { NotePencil } from "@/components/shared/icons";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { ContactMoreActions } from "./contact-more-actions";
import { useClientContactsQuery } from "@/gqlhooks/profile/useClientContactsQuery";
import { InfoCard } from "@/components/shared/info-card";
import { Typography } from "@/components/shared/typography";
import { useRoute } from "@/components/shared/router";
import { Skeleton } from "@/components/ui/skeleton";
import { Spin } from "@/components/shared/spin";

const ContactSkeleton = () => {
  return (
    <View className="flex flex-col gap-4 px-4 py-4 pb-12 md:px-0 md:pt-0">
      <View className="bg-info-background rounded-lg border border-info p-4">
        <Skeleton className="h-4 w-full" />
      </View>
      {Array.from({ length: 3 }).map((_, index) => (
        <View
          key={index}
          className="rounded-lg border border-border bg-card p-4"
        >
          <View className="flex flex-col gap-2">
            <View className="flex flex-row items-center gap-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-12 rounded-full" />
            </View>
            <View className="flex flex-row gap-2">
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-24" />
            </View>
          </View>
          <View className="flex flex-row gap-2 py-2">
            <Skeleton className="h-8 flex-1 rounded-full" />
            <Skeleton className="h-8 w-8 rounded-full" />
          </View>
        </View>
      ))}
    </View>
  );
};

const ContactActions = ({ primary, id }: { primary: boolean; id: string }) => {
  const { push } = useRoute();

  const handleClick = () => {
    push({
      pageKey: "contact",
      params: {
        id,
      },
    });
  };
  return (
    <View className="flex w-full flex-1 flex-row items-center gap-2">
      <View
        className="flex w-full flex-1 cursor-pointer flex-row items-center justify-center gap-1 rounded-full border border-primary-border p-2"
        onClick={handleClick}
      >
        <NotePencil className="size-5 text-primary" size={20} />
        <Typography variant="body-md" color="brand-primary">
          Edit Contact
        </Typography>
      </View>

      {!primary && <ContactMoreActions contactId={id} />}
    </View>
  );
};

export const ContactListContainer = () => {
  const { data: contacts, loading } = useClientContactsQuery();
  const { isMobile } = useWindowDimensions();

  if (loading) {
    return <ContactSkeleton />;
  }

  if (!contacts) return null;

  return (
    <View className="flex flex-col gap-4 px-4 py-4 pb-12 md:px-0 md:pt-0">
      <InfoCard description="Changing the phone number on a contact does not update your account login number. If you need to change your login number, please update it in your account settings." />
      {loading ? (
        <Spin />
      ) : (
        contacts?.map((item) => (
          <AccountInfoCard
            key={item.id}
            primary={item.primary}
            header={
              fullName({
                firstName: item.firstName,
                lastName: item.lastName,
              }) ?? ""
            }
            bodyText={item.email[0] ?? ""}
            subBodyText={item.phoneNumber[0] ?? ""}
            actions={<ContactActions primary={item.primary} id={item.id} />}
            direction={isMobile ? "col" : "row"}
          />
        ))
      )}
    </View>
  );
};
